'use client'

import { useEffect } from 'react'

/**
 * VersionChecker Component
 *
 * Automatically detects new deployments and refreshes the page to ensure
 * users always have the latest version without manual cache clearing.
 *
 * How it works:
 * 1. Stores current build ID in localStorage on first load
 * 2. Periodically checks for new build by fetching a version endpoint
 * 3. If build ID changes, performs a hard refresh to get new assets
 * 4. Uses service worker to cache bust on deployment
 */
export function VersionChecker() {
  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV !== 'production') {
      return
    }

    const BUILD_ID_KEY = 'orbitflow-build-id'
    const CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes

    async function checkVersion() {
      try {
        // Fetch build info with cache-busting query param
        const response = await fetch(`/api/version?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        })

        if (!response.ok) return

        const data = await response.json()
        const newBuildId = data.buildId

        if (!newBuildId) return

        // Get stored build ID
        const storedBuildId = localStorage.getItem(BUILD_ID_KEY)

        if (!storedBuildId) {
          // First visit - store build ID
          localStorage.setItem(BUILD_ID_KEY, newBuildId)
          return
        }

        // Check if build ID changed
        if (storedBuildId !== newBuildId) {
          console.log('[VersionChecker] New version detected, refreshing...')

          // Update stored build ID
          localStorage.setItem(BUILD_ID_KEY, newBuildId)

          // Clear all caches
          if ('caches' in window) {
            caches.keys().then((names) => {
              names.forEach((name) => caches.delete(name))
            })
          }

          // Hard refresh to get new assets
          window.location.reload()
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('[VersionChecker] Check failed:', error)
      }
    }

    // Check immediately on mount
    checkVersion()

    // Set up periodic checks
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL)

    // Check on focus (when user returns to tab)
    const handleFocus = () => checkVersion()
    window.addEventListener('focus', handleFocus)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  return null // This component doesn't render anything
}
